/**
 * The four props `EditorForm` needs from its host that are not the draft: the shelf, the bookmark,
 * whether the shelf is writing, and the reorder.
 *
 * Both hosts — `/raporty`'s editor and the public form builder's „Pytania" tab — wire them
 * identically, so the wiring lives here once. The bookmark writes the id the shelf minted back onto
 * the question: the row reads „already filed" off `sourceQuestionId`, and dropping the id let a
 * second press file the same question twice (`pages/reports/AGENTS.md`, the 17 September round).
 */

import type { CustomDraftItem, EditorAction, EditorDraft } from '../model/editorDraft';
import { customItems } from '../model/editorDraft';
import type { ShelfQuestion } from '../model/questionCatalog';
import type { TranslateKey } from '../model/translateKey';
import { useQuestionMove } from './useQuestionMove';
import { useQuestionShelf } from './useQuestionShelf';

export interface EditorFormWiring {
  readonly shelf: readonly ShelfQuestion[];
  readonly onBookmark: (item: CustomDraftItem) => void;
  readonly bookmarkBusy: boolean;
  readonly onMove: (id: string, targetIndex: number, label: string) => void;
}

export function useEditorFormWiring(options: {
  readonly draft: EditorDraft;
  readonly dispatch: (action: EditorAction) => void;
  readonly announce: (message: string) => void;
  readonly t: TranslateKey;
}): EditorFormWiring {
  const { draft, dispatch, announce, t } = options;
  const shelf = useQuestionShelf();
  const moves = useQuestionMove({
    t,
    count: customItems(draft).length,
    onMove: (id, targetIndex) => {
      dispatch({ type: 'move', id, targetIndex });
    },
    announce,
  });

  return {
    shelf: shelf.rows,
    onBookmark: (item) => {
      shelf.create(
        {
          label: item.label,
          answerType: item.answerType,
          options: item.options,
          required: item.required,
        },
        (id) => {
          if (id !== null) {
            dispatch({ type: 'updateCustom', id: item.id, patch: { sourceQuestionId: id } });
          }
          announce(t('web:reports.editor.question.bookmarked', { label: item.label }));
        },
      );
    },
    bookmarkBusy: shelf.saving,
    onMove: moves.move,
  };
}
